import { pillarArticles, type PillarArticle } from '@/mocks/articles';

const DEFAULT_SITE_URL = 'https://allchinabuyspread.beauty';

export const SITE_URL: string = (
  (import.meta.env.VITE_SITE_URL as string | undefined) || DEFAULT_SITE_URL
).replace(/\/+$/, '');

export const SITE_NAME = 'AllChinaBuy Spreadsheet';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=A%20vibrant%20editorial%20flatlay%20of%20colorful%20sneakers%2C%20folded%20streetwear%2C%20caps%2C%20bags%20and%20accessories%20arranged%20in%20a%20flowing%20diagonal%20composition%20on%20a%20clean%20off-white%20background%2C%20soft%20bright%20studio%20lighting%2C%20premium%20curated%20fashion%20collection%2C%20high%20detail%20product%20photography&width=1600&height=900&seq=hero-main-v2&orientation=landscape';

const ARTICLE_HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=A%20modern%20flat%20lay%20of%20an%20organized%20product%20research%20guide%20with%20category%20cards%2C%20seller%20profile%20sheets%20and%20QC%20photo%20printouts%20arranged%20on%20a%20bright%20clean%20desk%2C%20soft%20daylight%2C%20comprehensive%20buying%20guide%20concept%2C%20editorial%20top-down%20photography&width=1600&height=600&seq=article-hero-v2&orientation=landscape';

const PUBLISHED_DATE = '2026-08-26';
const MODIFIED_DATE = '2026-08-26';

export type JsonLd = Record<string, unknown>;

export interface RouteSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogType: string;
  ogImage?: string;
  robots?: string;
  jsonLd: JsonLd[];
}

function websiteSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    description:
      'Independent products, sellers, QC, shipping and buying resource for the AllChinaBuy Spreadsheet.',
  };
}

function organizationSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: FAVICON_URL,
  };
}

function articleSchema(input: {
  title: string;
  description: string;
  path: string;
  image?: string;
}): JsonLd {
  const schema: JsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.title,
    description: input.description,
    url: `${SITE_URL}${input.path}`,
    datePublished: PUBLISHED_DATE,
    dateModified: MODIFIED_DATE,
    author: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${input.path}` },
  };

  if (input.image) {
    schema.image = input.image;
  }

  return schema;
}

function pillarSeo(article: PillarArticle): RouteSeo {
  return {
    title: `${article.title} | AllChinaBuy Spreadsheet`,
    description: article.excerpt,
    keywords: `AllChinaBuy Spreadsheet, ${article.category}, buying guide, product research`,
    path: `/article/${article.slug}`,
    ogType: 'article',
    ogImage: article.imageUrl,
    jsonLd: [
      articleSchema({
        title: article.title,
        description: article.excerpt,
        path: `/article/${article.slug}`,
        image: article.imageUrl,
      }),
    ],
  };
}

export function resolveSeo(pathname: string): RouteSeo {
  const clean = pathname === '' ? '/' : pathname;

  if (clean === '/') {
    return {
      title: 'AllChinaBuy Spreadsheet 2026 | Products, Sellers, QC & Guides',
      description:
        'Explore the AllChinaBuy Spreadsheet for products, trusted sellers, QC guides, shipping costs, warehouse information, reviews, coupons and practical buying resources.',
      keywords:
        'AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse, AllChinaBuy legit, buying guide',
      path: '/',
      ogType: 'website',
      ogImage: HERO_IMAGE,
      jsonLd: [websiteSchema(), organizationSchema()],
    };
  }

  if (clean === '/article') {
    return {
      title:
        'AllChinaBuy Spreadsheet 2026 Complete Guide | Products, Sellers, QC & Buying Guide',
      description:
        'A complete guide to the AllChinaBuy Spreadsheet covering product categories, seller research, QC photos, shipping costs, warehouse storage and safe buying practices.',
      keywords:
        'AllChinaBuy Spreadsheet, AllChinaBuy guide, QC photos, seller research, shipping cost, warehouse, buying guide',
      path: '/article',
      ogType: 'article',
      ogImage: ARTICLE_HERO_IMAGE,
      jsonLd: [
        articleSchema({
          title: 'AllChinaBuy Spreadsheet 2026 Complete Guide',
          description:
            'A complete guide to the AllChinaBuy Spreadsheet covering product categories, seller research, QC photos, shipping costs, warehouse storage and safe buying practices.',
          path: '/article',
          image: ARTICLE_HERO_IMAGE,
        }),
      ],
    };
  }

  const match = clean.match(/^\/article\/([^/]+)$/);
  if (match) {
    const article = pillarArticles.find((a) => a.slug === match[1]);
    if (article) {
      return pillarSeo(article);
    }
  }

  return {
    title: 'Page not found | AllChinaBuy Spreadsheet',
    description:
      'The page you are looking for could not be found. Return to the AllChinaBuy Spreadsheet homepage to continue browsing.',
    keywords: 'AllChinaBuy Spreadsheet',
    path: clean,
    ogType: 'website',
    robots: 'noindex, nofollow',
    jsonLd: [],
  };
}